// src/components/layout/AppShell.jsx
import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { readLastRole } from "@/lib/roleCache";
import { writeLastAuthedPath } from "@/lib/authedPathCache";
import useIdleLogout from "@/hooks/useIdleLogout";
import IdleLogoutModal from "@/components/auth/IdleLogoutModal.jsx";

import DesktopHeaderTabs from "@/components/nav/DesktopHeaderTabs.jsx";
import MobileTabBar from "@/components/nav/MobileTabBar.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import {
  PHARMACY_NOTIFICATIONS_CHANGED_EVENT,
  readPharmacyNotifications,
} from "@/services/pharmacyLaneService.js";
import useDeviceAlerts from "@/hooks/useDeviceAlerts.js";
import useMessageAlerts from "@/hooks/useMessageAlerts.js";
import Snackbar from "@/sot/primitives/Snackbar.jsx";

function isBlockedPath(pathname = "") {
  return (
    pathname === "/" ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/auth/") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password")
  );
}

function shouldHideMobileTab(pathname) {
  return (
    pathname === "/appointments/payment" ||
    pathname.startsWith("/appointments/payment/") ||
    pathname.startsWith("/institutions") ||
    pathname.startsWith("/doctors/appointments/") ||
    pathname.startsWith("/appointments/prescriptions/") ||
    pathname.startsWith("/prescriptions/") ||
    pathname.startsWith("/profile-settings/doctor/p/availability") ||
    pathname.startsWith("/appointments/summary/") ||
    pathname.startsWith("/regional-health") ||
    pathname.startsWith("/settings/doctor/login-security/two-factor") ||
    pathname.startsWith("/settings/patient/login-security/two-factor") ||
    pathname.startsWith("/patient/p/") ||
    pathname.startsWith("/verify/rx/") ||
    pathname.startsWith("/pharmacies/scan-prescription") ||
    pathname.startsWith("/pharmacy/prescriptions/scan")
  );
}

function shouldFlushTop(pathname) {
  return (
    pathname.startsWith("/institutions") ||
    pathname.startsWith("/doctor/profile") ||
    pathname.startsWith("/patient/profile") ||
    pathname === "/messages" ||
    pathname.startsWith("/messages/")
  );
}

function shouldUseWhitePageSurface(pathname = "") {
  return (
    pathname === "/settings/doctor/notifications" ||
    pathname === "/messages" ||
    pathname.startsWith("/messages/")
  );
}

function shouldUseFullWidthContent(pathname = "") {
  return pathname.startsWith("/institutions");
}

function shouldManageOwnShellSpacing(pathname = "") {
  return pathname.startsWith("/institutions") || pathname === "/messages" || pathname.startsWith("/messages/");
}

function isLiveConsultationRoute(pathname = "") {
  return (
    pathname.startsWith("/appointments/video/") ||
    pathname.startsWith("/appointments/call/")
  );
}

function normalizeShellRole(value) {
  const role = String(value || "").toLowerCase().trim();
  return role === "doctor" || role === "patient" || role === "admin" || role === "pharmacy"
    ? role
    : "";
}

function MobileTabSpacer() {
  return <div className="h-[calc(96px+env(safe-area-inset-bottom))]" aria-hidden="true" />;
}

export default function AppShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = React.useRef(null);
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const userId = auth?.user?.id || null;
  const loading = !!auth?.loading;
  const isAuthed = Boolean(auth?.user || auth?.profile || auth?.session || auth?.bypass);
  const storedRole = readLastRole(userId);
  const shellRole =
    normalizeShellRole(auth?.profile?.role) ||
    normalizeShellRole(auth?.role) ||
    normalizeShellRole(auth?.user?.app_metadata?.role) ||
    storedRole;
  const isMessagesRoute = location.pathname === "/messages" || location.pathname.startsWith("/messages/");
  const isInstitutionShell = location.pathname.startsWith("/institutions");
  const isPharmacyShell =
    location.pathname.startsWith("/pharmacy") || (shellRole === "pharmacy" && isMessagesRoute);
  const isLiveConsultation = isLiveConsultationRoute(location.pathname);
  const [pharmacyNotifications, setPharmacyNotifications] = React.useState(() =>
    readPharmacyNotifications()
  );

  useDeviceAlerts({
    userId,
    role: shellRole,
    pharmacyNotifications,
  });
  const { alert: messageAlert, dismissAlert } = useMessageAlerts({
    userId,
    role: shellRole,
  });

  const hideMobile = shouldHideMobileTab(location.pathname);
  const flushTop = shouldFlushTop(location.pathname);
  const whitePageSurface = shouldUseWhitePageSurface(location.pathname);
  const fullWidthContent = shouldUseFullWidthContent(location.pathname);
  const manageOwnShellSpacing = shouldManageOwnShellSpacing(location.pathname);
  const showMobileTab = isAuthed && !hideMobile && !isPharmacyShell && !isInstitutionShell;

  React.useEffect(() => {
    if (!isPharmacyShell) return undefined;

    const refreshNotifications = () => {
      setPharmacyNotifications(readPharmacyNotifications());
    };

    refreshNotifications();
    window.addEventListener("storage", refreshNotifications);
    window.addEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);

    return () => {
      window.removeEventListener("storage", refreshNotifications);
      window.removeEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
    };
  }, [isPharmacyShell]);

  React.useEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0, left: 0 });
    window.scrollTo?.({ top: 0, left: 0 });
  }, [location.pathname]);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const html = document.documentElement;
    const body = document.body;
    if (!showMobileTab) return undefined;

    html.classList.add("afrimed-auth-mobile-shell");
    body?.classList.add("afrimed-auth-mobile-shell");

    return () => {
      html.classList.remove("afrimed-auth-mobile-shell");
      body?.classList.remove("afrimed-auth-mobile-shell");
    };
  }, [showMobileTab]);

  React.useEffect(() => {
    if (!userId) return;
    if (isBlockedPath(location.pathname || "")) return;

    const fullPath =
      (location?.pathname || "/") +
      (location?.search || "") +
      (location?.hash || "");

    try {
      writeLastAuthedPath(fullPath, userId);
    } catch {
      // ignore localStorage write failures
    }
  }, [userId, location?.pathname, location?.search, location?.hash]);

  const idle = useIdleLogout({
    enabled: !isLiveConsultation,
    isAuthed: !!userId && !loading,
    idleMs: 25 * 60 * 1000,
    graceMs: 2 * 60 * 1000,
    redirectTo: "/login?logout=idle",
  });

  return (
    <div
      className={[
        "fixed inset-0 flex h-[100dvh] w-full min-w-0 flex-col overflow-hidden md:static md:inset-auto md:h-auto md:min-h-[100dvh] md:overflow-visible",
        whitePageSurface
          ? "bg-[rgb(var(--clr-surface))]"
          : "bg-[rgb(var(--afrimed-shell-background))]",
      ].join(" ")}
    >
      {isAuthed && !isInstitutionShell ? (
        isPharmacyShell ? (
          <PharmacyDesktopHeader
            section={isMessagesRoute ? "messages" : "dashboard"}
            notifications={pharmacyNotifications}
          />
        ) : (
          <DesktopHeaderTabs />
        )
      ) : null}

      {/* ✅ RAIL: 720 on mobile, 1008 already on md+ (not lg) */}
      <main
        ref={mainRef}
        className={[
          "afrimed-app-content min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] md:overflow-visible",
          fullWidthContent ? "w-full" : "afrimed-rail afrimed-rail--comfortable",
          flushTop ? "pt-0" : "pt-5",
          manageOwnShellSpacing ? "pb-0" : "pb-6",
        ].join(" ")}
      >
        {children ? children : <Outlet />}
        {showMobileTab && !manageOwnShellSpacing ? <MobileTabSpacer /> : null}
      </main>

      {showMobileTab ? <MobileTabBar /> : null}

      <IdleLogoutModal
        open={idle.open}
        secondsLeft={idle.secondsLeft}
        onStay={idle.staySignedIn}
        onLogout={idle.logoutNow}
      />

      <Snackbar open={!!messageAlert} onClose={dismissAlert} duration={4000}>
        <button
          type="button"
          onClick={() => {
            if (!messageAlert?.route) {
              dismissAlert();
              return;
            }
            dismissAlert();
            navigate(messageAlert.route, {
              state: messageAlert.routeState || null,
            });
          }}
          className="flex w-full items-start gap-3 text-left"
        >
          <span className="mt-0.5 inline-flex h-2.5 w-2.5 shrink-0 rounded-full bg-[#34d399]" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">
              {messageAlert?.title || "New message"}
            </span>
            <span className="block truncate text-sm text-white/85">
              {messageAlert?.body || "Open AfriMed Messages to view the latest message."}
            </span>
          </span>
        </button>
      </Snackbar>
    </div>
  );
}
